import React from 'react';
import {priority} from '@priority-software/priority-proto-api/lib';
import {Dialog, PriorityIcon} from '@priority-software/priority-style-react';
import {observer} from 'mobx-react';
import {useState, useRef, useEffect} from 'react';
import {Attachments} from '../../Attachments/components/Attachments';
import {History} from '../../History/components/History';
import {TriggerButton} from './TriggerButton';
import classNames from 'classnames';
import {
    getEnabledColumns,
    setEnabledColumns,
    monitorAction,
    handleFeaturePromotion,
    closeFeaturePopover
} from '../actions';
import {SettingsPanelCol, MonitoredActions, fetchMax} from '../consts';
import {formsStore as legacyStore} from '../../../../../../_legacy/Form/stores/FormsStore';
import {APIActionStatus, Screen, formsStore as store} from '../../../../../../stores/Form';
import {
    getGWTIsQueryMode,
    isActiveRowEmpty,
    getValueByColName
} from '../../../../../../_shared/GWTBridge/invoke/formTable';
import {closeFormBySession} from '../../../../actions/form';
import {returnFocus} from '../../../../../../_shared/GWTBridge/invoke/genPage';
import {getServerTexts} from '../../texts';
import {saveActiveRow} from '../../../../../../_legacy/Form/services/formService';
import styles from '../styles/sidePanelButtons.module.scss';
import {
    isCurrentRowNew,
    refreshNetform,
    setIsForceSendValueToServer,
    setIsReactPopupOpen
} from '../../../../../../_shared/GWTBridge/invoke/netForm';
import {runBridgeForm} from '../../../../../../features/Automations/actions/bridgeActions/actions';
import {startAndFetchSubform} from '../../../../actions/batchedActions/standAloneFormBatch';

export const SidePanelButtons = observer(({sidePanelSubforms}) => {
    const [isLoading, setIsLoading] = useState(false);
    const [isOpen, setIsOpen] = useState(false);
    const [subformType, setSubformType] = useState<priority.netitems.form.SubformType>(null);
    const [enabledCols, setEnabledCols] = useState<string[]>(null);
    const parentTitle = useRef('');
    const isLegacyStore = useRef(null);
    const standAloneSession = useRef(null);
    const currentScreen = useRef(null);
    const mainFormValue = useRef('');
    const statusColorMap = useRef(null);
    const attachmentsButtonRef = useRef();
    const historyButtonsRef = useRef();

    const isParentFormMain = useRef(false);
    const parentNeedsRefresh = useRef(false);

    const setParentNeedsRefresh = value => {
        parentNeedsRefresh.current = value;
    };

    useEffect(() => {
        isLegacyStore.current = !!legacyStore.currentSession;
        currentScreen.current = store.currentScreen;
        return () => {
            isLegacyStore.current &&
                standAloneSession.current &&
                closeFormBySession(standAloneSession.current, false);
        };
    }, []);

    useEffect(() => {
        setIsReactPopupOpen(isOpen);

        return () => setIsReactPopupOpen(false);
    }, [isOpen]);

    useEffect(() => {
        if (!store.currentScreen && !isLoading) {
            setIsOpen(false);
        }
    }, [store.currentScreen, isLoading]);

    useEffect(() => {
        if (sidePanelSubforms.isDisabled) {
            const isDisabled = sidePanelSubforms.isDisabled === '1';
            if (!isDisabled) {
                handleFeaturePromotion({
                    attachmentsButtonRef,
                    historyButtonsRef,
                    initialHistorySubformType,
                    openSubForm: handleClick
                });
            } else {
                if (attachmentsButtonRef.current || historyButtonsRef.current) {
                    closeFeaturePopover();
                }
            }
        }
    }, [sidePanelSubforms]);

    enum panelType {
        Attachments = 'Attachments',
        History = 'History'
    }

    const attachmentsSubformName =
        sidePanelSubforms[priority.netitems.form.SubformType.Attachments];
    const statusesSubformName = sidePanelSubforms[priority.netitems.form.SubformType.StatusLog];
    const changesSubformName = sidePanelSubforms[priority.netitems.form.SubformType.ChangeLog];

    const panelsMapping = {
        [priority.netitems.form.SubformType.Attachments]: panelType.Attachments,
        [priority.netitems.form.SubformType.StatusLog]: panelType.History,
        [priority.netitems.form.SubformType.ChangeLog]: panelType.History
    };

    const getFormZoomValue = form => {
        const row = form.currentRow;
        const records = form.records;
        return records[row].fieldsMap.get(form.form.ZoomField as number)?.currentValue || '';
    };

    const openSidePanel = async (subformType: priority.netitems.form.SubformType) => {
        const subformName = sidePanelSubforms[subformType];

        let isOpenBlocked = false;
        if (!isOpen) {
            setParentNeedsRefresh(false);
            isParentFormMain.current = !legacyStore.currentForm?.parentForm;
            getServerTexts();

            if (isLegacyStore.current) {
                // old UI
                if (!isActiveRowEmpty() && !getGWTIsQueryMode()) {
                    if (isCurrentRowNew()) {
                        await saveActiveRow();
                    }
                    setSubformType(subformType);
                    setIsOpen(true);
                    setIsLoading(true);
                    await runBridgeForm(true);
                    const form = legacyStore.currentForm;
                    const colorsMap = Array.from(form.statuses, function (entry) {
                        return {Statdes: entry[0], Color: entry[1]};
                    });
                    statusColorMap.current = colorsMap;
                    parentTitle.current = store.currentScreen?.getActiveForm().title;
                    const zoomFieldName = form.columnsMap.get(form.zoomField)?.name;
                    mainFormValue.current = getValueByColName(zoomFieldName);
                    const isFormOk = await startAndFetchSubform(
                        store.currentScreen?.getActiveForm(),
                        subformName,
                        fetchMax
                    );
                    standAloneSession.current = store.currentScreen?.session;

                    if (!isFormOk) {
                        closeSidePanel();
                    }
                } else {
                    isOpenBlocked = true;
                }
            } else {
                //change this when the subform will be a separate session
                const activeForm = store.currentScreen.getActiveForm();
                if (
                    store.currentScreen.form.currentRecord?.keys &&
                    activeForm.searchModeLevel === 0
                ) {
                    setSubformType(subformType);
                    setIsOpen(true);
                    setIsLoading(true);
                    if (
                        subformType === priority.netitems.form.SubformType.ChangeLog ||
                        priority.netitems.form.SubformType.StatusLog
                    ) {
                        mainFormValue.current = getFormZoomValue(activeForm);
                        statusColorMap.current = activeForm.statuses;
                    }
                    parentTitle.current = activeForm.title;
                    const isOk = await store.currentScreen.runSubForm(
                        subformName,
                        store.currentScreen.getActiveForm()
                    );
                    if (isOk === APIActionStatus.blocked) {
                        isOpenBlocked = true;
                    }
                } else {
                    isOpenBlocked = true;
                }
            }
            const enabledCols = await getEnabledColumns(subformName);
            setEnabledCols(enabledCols);
            setIsLoading(false);
        } else {
            setIsOpen(false);
        }
        isOpenBlocked && setIsOpen(false);
        return !isOpenBlocked;
    };

    const closeSidePanel = async () => {
        if (!store.currentScreen) {
            return;
        }
        if (isLegacyStore.current) {
            await closeFormBySession(store.currentScreen.session, false);
            returnFocus();
            if (parentNeedsRefresh.current) {
                if (isLegacyStore.current) {
                    if (isParentFormMain.current) {
                        refreshNetform();
                    } else {
                        setIsForceSendValueToServer(true);
                    }
                } else {
                    //** TODO: refresh for new UI */
                }
            }
        } else {
            leaveSubform();
        }
        parentTitle.current = '';
        mainFormValue.current = '';
        setIsOpen(false);
        setSubformType(null);
    };

    const leaveSubform = async () => {
        const screen: Screen = currentScreen.current;
        await screen.checkLevel(screen.activeLevel - 1);
        await screen.setActiveLevel(screen.activeLevel - 1);
        screen.getActiveForm()?.setSubForm(screen.getActiveForm()?.activeTabSubForm);
    };

    const setDialogContent = () => {
        const records = store.currentScreen?.getActiveForm()?.records.filter(r => !r.isNew);
        switch (subformType) {
            case priority.netitems.form.SubformType.Attachments:
                return (
                    <Attachments
                        files={records}
                        parentTitle={parentTitle.current}
                        isLoading={isLoading}
                        shouldFetchMore={store.currentScreen?.getActiveForm()?.shouldFetchMore}
                        fastSearchQuery={store.currentScreen?.getActiveForm().getFastSearchQuery()}
                        enabledCols={enabledCols}
                        applySettings={applySettings}
                        subformType={subformType}
                        formTitle={store.currentScreen?.getActiveForm()?.form?.Title}
                        setParentNeedsRefresh={setParentNeedsRefresh}
                    />
                );
            case priority.netitems.form.SubformType.StatusLog:
            case priority.netitems.form.SubformType.ChangeLog:
                return (
                    <History
                        fastSearchQuery={store.currentScreen?.getActiveForm().getFastSearchQuery()}
                        records={records}
                        parentTitle={parentTitle.current}
                        mainFormValue={mainFormValue.current}
                        isLoading={isLoading}
                        statusColorMap={statusColorMap.current}
                        shouldFetchMore={store.currentScreen?.getActiveForm()?.shouldFetchMore}
                        sidePanelSubforms={sidePanelSubforms}
                        form={store.currentScreen?.getActiveForm()}
                        switchSubform={switchSubform}
                        isLegacyStore={isLegacyStore.current}
                        enabledCols={enabledCols}
                        applySettings={applySettings}
                        subformType={subformType}
                    />
                );
            case priority.netitems.form.SubformType.InternalDialog:
            default:
                return null;
        }
    };

    const applySettings = async (cols: SettingsPanelCol[]) => {
        const formName = sidePanelSubforms[subformType];
        const enabledColumns = await setEnabledColumns(formName, cols);
        setEnabledCols(enabledColumns);
    };

    const switchSubform = async (subformType: priority.netitems.form.SubformType) => {
        const parentForm = store.currentScreen?.getActiveForm()?.parentForm;
        if (!parentForm) {
            return;
        }
        setIsLoading(true);
        await store.currentScreen.runSubForm(sidePanelSubforms[subformType], parentForm);
        const enabledCols = await getEnabledColumns(sidePanelSubforms[subformType]);
        setEnabledCols(enabledCols);
        setSubformType(subformType);
        setIsLoading(false);
    };

    const monitorOpenForm = (
        subformType: priority.netitems.form.SubformType,
        action: MonitoredActions
    ) => {
        const subformName = sidePanelSubforms[subformType];
        monitorAction({subformType, action, subformName});
    };

    const handleClick = (type: priority.netitems.form.SubformType) => {
        if (isLoading) {
            return;
        }
        if (!isOpen) {
            openSidePanel(type);
            monitorOpenForm(type, MonitoredActions.Open);
        } else if (panelsMapping[type] === panelsMapping[subformType]) {
            closeSidePanel();
        } else {
            switchSubform(type);
            monitorOpenForm(type, MonitoredActions.Open);
        }
    };

    const initialHistorySubformType = statusesSubformName
        ? priority.netitems.form.SubformType.StatusLog
        : priority.netitems.form.SubformType.ChangeLog;

    return (
        <>
            <div
                className={classNames(styles.triggerButtons, {
                    [styles.openedTriggerButtons]: isOpen
                })}
            >
                {attachmentsSubformName && (
                    <TriggerButton
                        className={styles.icon}
                        icon={PriorityIcon.attachment}
                        disabled={sidePanelSubforms.isDisabled === '1'}
                        onClick={() => {
                            handleClick(priority.netitems.form.SubformType.Attachments);
                        }}
                        isActive={panelsMapping[subformType] === panelType.Attachments}
                        ref={attachmentsButtonRef}
                    />
                )}
                {(statusesSubformName || changesSubformName) && (
                    <TriggerButton
                        className={styles.icon}
                        icon={PriorityIcon.history}
                        disabled={sidePanelSubforms.isDisabled === '1'}
                        onClick={() => {
                            handleClick(initialHistorySubformType);
                        }}
                        isActive={panelsMapping[subformType] === panelType.History}
                        ref={historyButtonsRef}
                    />
                )}
            </div>

            {isOpen && (store.currentScreen || isLoading) && (
                <Dialog
                    componentClasses={styles.panel}
                    isOpen={true}
                    onClose={closeSidePanel}
                    width={450}
                    mask={false}
                    maskClosable
                    content={setDialogContent()}
                />
            )}
        </>
    );
});
